import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Modal,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Text,
  Platform,
} from "react-native";
import LottieView from "lottie-react-native";
import { AppTheme } from "../../utils/theme";

interface Props {
  coins: number | string | any;
  visible?: boolean;
  onClose?: () => void;
}

const AnimatedLottieModal = ({
  coins,
  visible = true,
  onClose,
}: Props) => {
  const numCoins = Number(coins) || 0;
  const [showText, setShowText] = useState(false);
  const [lottieCoins, setLottieCoins] = useState(false);

  // Modal animations
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  // Text animations
  const textOpacity = useRef(new Animated.Value(0)).current;
  const textTranslateY = useRef(new Animated.Value(20)).current;

  // Modal open / close animation
  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 6,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  // Text animation after Lottie
  useEffect(() => {
    if (showText) {
      Animated.parallel([
        Animated.timing(textOpacity, {
          toValue: 1,
          duration: 400,
          useNativeDriver: true,
        }),
        Animated.timing(textTranslateY, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [showText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLottieCoins(true);
      setShowText(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    Animated.parallel([
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onClose?.();
    });
  };

  return (
    <Modal transparent visible={visible} animationType="none" onRequestClose={handleClose}>
      <Animated.View style={[styles.overlay, { opacity: opacityAnim }]}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={handleClose} />

        <Animated.View
          style={[styles.modal, { transform: [{ scale: scaleAnim }] }]}
        >
          {numCoins > 0 ? (
            <View style={styles.lottieContainer}>
              <LottieView
                source={require("../../assets/animations/Coins_2.json")}
                autoPlay
                loop={false}
                style={styles.lottie}
                onAnimationFinish={() => setShowText(true)}
              />
              {lottieCoins && (
                <View style={styles.secondLottieContainer}>
                  <LottieView
                    source={require("../../assets/animations/Coins_3.json")}
                    autoPlay
                    loop={false}
                    style={styles.lottie}
                    onAnimationFinish={() => setShowText(true)}
                  />
                </View>
              )}
            </View>
          ) : (
            <View style={styles.lottieContainer}>
              <LottieView
                source={require("../../assets/animations/Sad.json")}
                autoPlay
                loop={false}
                style={styles.lottie1}
                onAnimationFinish={() => setShowText(true)}
              />
            </View>
          )}

          {showText && (
            <Animated.View
              style={[
                styles.textContainer,
                {
                  opacity: textOpacity,
                  transform: [{ translateY: textTranslateY }],
                },
              ]}
            >
              <Text style={styles.titleText}>
                {numCoins > 0 ? "Congratulations!" : "Try Again"}
              </Text>
              <Text style={styles.coinText}>
                {numCoins > 0
                  ? `You won ${numCoins} Coins`
                  : `Better luck next time`}
              </Text>
            </Animated.View>
          )}

          <TouchableOpacity
            style={styles.okButton}
            activeOpacity={0.8}
            onPress={handleClose}
          >
            <Text style={styles.okButtonText}>CONTINUE</Text>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

export default AnimatedLottieModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.75)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modal: {
    width: "90%",
    maxWidth: 400,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 10000,
  },
  lottieContainer: {
    width: 320,
    height: 320,
    justifyContent: "center",
    alignItems: "center",
  },
  lottie: {
    width: 300,
    height: 300,
  },
  lottie1: {
    width: 250,
    height: 250,
  },
  secondLottieContainer: {
    position: "absolute",
  },
  textContainer: {
    marginTop: 10,
    alignItems: "center",
  },
  titleText: {
    fontSize: 28,
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
    textAlign: "center",
  },
  coinText: {
    marginTop: 6,
    fontSize: 24,
    color: "#FFD700",
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    textAlign: "center",
    textShadowColor: "rgba(0,0,0,0.5)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  okButton: {
    marginTop: 25,
    backgroundColor: "#FF9900",
    paddingHorizontal: 40,
    paddingVertical: 12,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: "#FFE066",
  },
  okButtonText: {
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 22,
    textAlign: "center",
  },
});